import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Plus } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TrainerCard } from '@/features/TrainerLayout/primitives/TrainerCard';
import { useTrainerHackathons } from '@/lib/hooks/useHackathons';
import { HackathonCreateDialog } from '@/features/Trainer/HackathonCreateDialog';
import type { HackathonStatus } from '@/lib/api/hackathons';

// The two surfaces that reuse this view. Both grant full trainer access to the
// same batch-hackathons endpoints (super_admin is allowed on the trainer route
// group server-side), so the only per-route difference is where the detail
// link points.
type HackathonDetailTo =
  | '/trainer/batches/$batchId/hackathons/$hackathonId'
  | '/admin/batches/$batchId/hackathons/$hackathonId';

// Mirror the org-admin view's status→variant mapping (see BatchHackathonsTab)
// so the same lifecycle status renders identically across trainer and org
// surfaces. `live` is the active state → primary (`default`); the rest are quieter.
const STATUS_VARIANT: Record<HackathonStatus, 'default' | 'secondary' | 'outline'> = {
  draft: 'secondary',
  scheduled: 'outline',
  live: 'default',
  completed: 'secondary',
};

/**
 * Route-agnostic hackathons list + create surface. It takes `batchId` and the
 * detail-route target as props instead of reading them from a route-bound
 * `Route.useParams()`, so the same UI can be mounted under both the trainer and
 * platform-admin batch route trees.
 */
export function HackathonsView({
  batchId,
  detailTo,
}: {
  batchId: string;
  detailTo: HackathonDetailTo;
}) {
  const { data: hackathons, isLoading } = useTrainerHackathons(batchId);
  const [createOpen, setCreateOpen] = useState(false);

  if (isLoading) {
    return <div className="p-6 text-sm text-[#6b7280]">Loading hackathons…</div>;
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[#111827]">Hackathons</h2>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" /> New Hackathon
        </Button>
      </div>

      {!hackathons?.length ? (
        <TrainerCard>
          <p className="py-8 text-center text-sm text-[#6b7280]">
            No hackathons yet. Create one after finishing a module to get students competing.
          </p>
        </TrainerCard>
      ) : (
        <div className="grid gap-3">
          {hackathons.map((h) => (
            <Link
              key={h.id}
              to={detailTo}
              params={{ batchId, hackathonId: h.id }}
              className="block rounded-[24px] transition-shadow hover:drop-shadow-[0px_2px_6px_rgba(0,0,0,0.08)]"
            >
              <TrainerCard className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[#111827]">{h.title}</p>
                    <p className="text-xs text-[#6b7280]">
                      {new Date(h.start_at).toLocaleString()} → {new Date(h.end_at).toLocaleString()}
                    </p>
                  </div>
                  <Badge
                    variant={STATUS_VARIANT[h.status] ?? STATUS_VARIANT.draft}
                    className="flex-shrink-0 capitalize"
                  >
                    {h.status}
                  </Badge>
                </div>
              </TrainerCard>
            </Link>
          ))}
        </div>
      )}

      <HackathonCreateDialog batchId={batchId} open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
